/**
 * A study or job period, as a reader says it: "Jan 1995 – Jan 1999",
 * "2019 – Present".
 *
 * The profile stores what the onboarding form and the CV import write —
 * "1995-01", "2019", sometimes a full ISO date — and the cards printed it
 * as stored ("1995-01 - 1999-01"); work experience printed no period at all.
 */

type FormatDate = (date: Date, format: Intl.DateTimeFormatOptions) => string;

/** A stored period bound as a date, and whether it named the month. */
function parseBound(value: string | undefined): { date: Date; hasMonth: boolean } | null {
  const match = /^(\d{4})(?:-(\d{1,2}))?/.exec(value?.trim() ?? '');
  if (!match) return null;
  const year = Number(match[1]);
  const month = match[2] ? Number(match[2]) - 1 : 0;
  return { date: new Date(year, month, 1), hasMonth: !!match[2] };
}

/**
 * The period in words, or null when it has no start and no end. An open end
 * reads as `openEnd` ("Present" for a job, "Ongoing" for studies).
 */
export function formatPeriod(
  start: string | undefined,
  end: string | undefined,
  formatDate: FormatDate,
  openEnd: string
): string | null {
  const from = parseBound(start);
  const to = parseBound(end);
  if (!from && !to) return null;
  const say = (bound: { date: Date; hasMonth: boolean }) =>
    formatDate(
      bound.date,
      bound.hasMonth ? { month: 'short', year: 'numeric' } : { year: 'numeric' }
    );
  if (!from) return to ? say(to) : null;
  return `${say(from)} – ${to ? say(to) : openEnd}`;
}
